import React, { useState } from 'react'
import { Box, Typography, Card, Backdrop, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Damage } from '../../types/lease'
import { toMMDDYYYY } from '../../helpers/date'

const StyledImage = styled('img')({
    width: '70px',
    height: '70px',
    objectFit: 'cover',
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
        border: '1px solid rgba(255,255,255,0.3)',
        transform: 'scale(1.05)',
    },
})

interface DamageRowProps {
    damage: Damage
}

export const DamageRow: React.FC<DamageRowProps> = ({ damage }) => {
    const [fullScreenImage, setFullScreenImage] = useState<string | null>(null)

    // Calculate cost for tenant: (damage_config.cost * damage.amount) / number of damage_transactions
    const calculateTenantCost = (): number => {
        if (damage.damage_transactions.length === 0) {
            return 0
        }
        return (damage.cost * damage.amount) / damage.damage_transactions.length
    }

    const tenantCost = calculateTenantCost()
    let damageDescription = ''
    if (damage.damage_config) {
        damageDescription = damage.damage_config.description
    } else if (damage.description) {
        damageDescription = damage.description
    } else {
        damageDescription = 'Unknown Damage'
    }
    const createdDate = toMMDDYYYY(new Date(damage.created_date))
    const areaDisplay = `${damage.area_config_name}${damage.area_label ? ` - ${damage.area_label}` : ''}`

    return (
        <>
            <Card
                sx={{
                    bgcolor: 'rgba(138, 142, 140, 0.16)',
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid rgba(255,255,255,0.1)',
                    p: 2.5,
                }}
            >
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontWeight: 500,
                                flex: 1,
                            }}
                        >
                            {damageDescription}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                ml: 2,
                            }}
                        >
                            {createdDate}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.5)',
                                }}
                            >
                                Area:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                }}
                            >
                                {areaDisplay}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.5)',
                                }}
                            >
                                Cost:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    fontWeight: 500,
                                }}
                            >
                                ${tenantCost.toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>

                    {damage.attachments && damage.attachments.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 2,
                            }}
                        >
                            {damage.attachments.map((attachment, index) => (
                                <StyledImage
                                    key={attachment.id || index}
                                    src={attachment.file}
                                    alt={`Damage attachment ${index + 1}`}
                                    onClick={() => setFullScreenImage(attachment.file)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Card>

            {fullScreenImage && (
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        flexDirection: 'column',
                    }}
                    open={!!fullScreenImage}
                    onClick={() => setFullScreenImage(null)}
                >
                    <img
                        src={fullScreenImage}
                        style={{
                            maxHeight: '80%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                        }}
                        alt="Full screen damage attachment"
                    />
                    <Button
                        color="error"
                        sx={{
                            flex: 1,
                            maxHeight: 50,
                            mt: 2,
                            width: '80%',
                            borderRadius: 1,
                        }}
                        variant="contained"
                        onClick={() => setFullScreenImage(null)}
                    >
                        Close
                    </Button>
                </Backdrop>
            )}
        </>
    )
}

